import { NavLink, Link, Outlet, Navigate } from "react-router-dom";
import { CalendarDays, Heart, User, Star, Gift, CreditCard, Users, Package, Repeat, LogOut, Wallet } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const links = [
  { to: "/account/appointments", label: "Appointments", icon: CalendarDays },
  { to: "/account/favorites", label: "Favorites", icon: Heart },
  { to: "/account/packages", label: "Packages", icon: Package },
  { to: "/account/wallet", label: "Wallet", icon: Wallet },
  { to: "/account/reviews", label: "Reviews", icon: Star },
  { to: "/account/loyalty", label: "Loyalty cards", icon: CreditCard },
  { to: "/account/memberships", label: "Memberships", icon: Repeat },
  { to: "/account/gift-cards", label: "Gift cards", icon: Gift },
  { to: "/account/family", label: "Family & Friends", icon: Users },
  { to: "/account/profile", label: "Profile", icon: User },
];

const footerLinks = [
  { to: "/for-business", label: "List your business" },
  { to: "/legal/terms", label: "Terms of Service" },
  { to: "/legal/privacy", label: "Privacy Policy" },
];

export default function AccountLayout() {
  const { user, loading, logout } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/account" replace />;

  const initial = user.full_name?.trim()?.[0]?.toUpperCase() || "?";

  return (
    <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 px-4 py-4 sm:gap-8 sm:px-6 sm:py-8 md:grid-cols-[240px_1fr]">
      <div>
        <div className="mb-4 flex items-center gap-3 px-1 sm:mb-6">
          {user.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.full_name}
              className="h-14 w-14 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="brand-gradient flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-xl font-bold text-white">
              {initial}
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate font-montserrat text-base font-bold text-slate-900">
              {user.full_name}
            </p>
            {user.phone && <p className="truncate text-sm text-muted-foreground">{user.phone}</p>}
          </div>
        </div>

        <nav className="-mx-4 flex gap-1 overflow-x-auto border-b border-border px-4 pb-2 sm:mx-0 sm:flex-col sm:overflow-visible sm:border-0 sm:px-0 sm:pb-0 sm:space-y-1">
          {links.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium whitespace-nowrap ${
                  isActive ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-muted"
                }`
              }
            >
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={logout}
            className="flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-600 hover:bg-muted whitespace-nowrap sm:w-full"
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </nav>

        <div className="mt-4 space-y-1 border-t border-border pt-4 sm:mt-6">
          {footerLinks.map(({ to, label }) => (
            <Link
              key={label}
              to={to}
              className="block rounded-xl px-3 py-2 text-sm text-slate-500 hover:bg-muted hover:text-slate-700"
            >
              {label}
            </Link>
          ))}
        </div>
      </div>
      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  );
}
